/**
 * Google Analytics 4 Integration
 * Uses a consistent event schema across GA4 and the first-party funnel
 */
// Import via '@/lib/tracking' — direct imports are deprecated (see lib/tracking/index.ts).

import { getOrCreateTlsUid } from './tls-uid';
import { getRequestProof } from './request-proof';
import { getReferrerData, isGA4TrackingEnabled } from './ga4-tracking';
import { isAnalyticsAllowedNow } from '@/components/ui/cookie-consent';
import { normalizeSearchQuery } from './search-query';
import type {
  AdSlotState,
  CREATE_STEPS,
  CREATE_TOOLS,
  DownloadFailureReason,
  DownloadSource,
  GuideProblem,
  GuideSection,
  OfferSurface,
} from './funnel-property-enums';
import type { CarStatus } from './check-my-car';
import { REFERRER_HOST_UNKNOWN, referrerHostFromUrl } from './referrer-host';

declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'js' | 'consent',
      action: string | Date,
      params?: Record<string, string | number | boolean | undefined>
    ) => void;
  }
}

// Events we also mirror to the first-party /api/funnel/event endpoint so we
// can run our own funnel reports without depending on GA4 export.
// Exported (not just for internal use) so app/api/funnel/event/route.test.ts
// can assert parity against that route's ALLOWED_EVENTS allow-list — the two
// sets are maintained independently and a silent drift between them means an
// event fires client-side, the server 200s, and nothing ever lands in
// funnel_events. See that test file for the regression this guards against.
export const FIRST_PARTY_FUNNEL_EVENTS = new Set<string>([
  // TES-1573: top-of-funnel denominator. Without a page-view step every
  // conversion rate below was being read against downloads (or against
  // nothing at all) — landing→preview and landing→download were not
  // computable from first-party data. `landing_view` fires once per session
  // from a single global mount point; `library_page_viewed` /
  // `sound_page_viewed` were already firing client-side from their page
  // mounts but were missing from both allow-lists, so they never left the
  // browser.
  'landing_view',
  'library_page_viewed',
  'sound_page_viewed',
  'preview_play',
  'preview_completed',
  'preview_skipped',
  'download_button_click',
  'download_initiated',
  'download_completed',
  'install_guide_viewed',
  'guide_visited_post_download',
  'install_self_reported_complete',
  'email_file_requested',
  // Panel-impression denominator: without this, first-party data can't tell
  // "post-download panel never seen" from "seen and ignored" — the two have
  // opposite fixes. Every stage below download_completed was being read
  // against downloads instead of against panel impressions.
  'download_confirm_shown',
  // TES-1006 §3: share-card referral funnel.
  'referral_link_generated',
  'referral_link_copied',
  // Interactions that previously reached GA4 only (audit N11 / MEAS-1).
  'sound_detail_click',
  'saved_sounds_save_click',
  'saved_sounds_unsave_click',
  'saved_sounds_download_click',
  'saved_sounds_swap_pack_download',
  'library_zero_results_viewed',
  'library_zero_results_recovery_click',
  'trending_section_view',
  // Measurement gaps (audit N11 / MEAS-2, MEAS-3).
  'library_search',
  'download_failed',
  'create_step',
  'faq_page_viewed',
  // Guide and ad instrumentation (audit X12 / MEAS-4, MEAS-5).
  'guide_section_view',
  'guide_troubleshoot_open',
  'guide_car_check',
  'ad_slot_state',
  // Return loop (audit X9 / LOOP-1..3).
  'newsletter_offer_shown',
  'newsletter_signup',
  'saved_list_emailed',
  'new_since_line_click',
]);

interface FirstPartyFunnelPayload {
  event: string;
  properties: Record<string, string | number | boolean>;
  page: string;
  ts: number;
}

// Must not exceed MAX_BATCH_SIZE in app/api/funnel/event/route.ts. Each flush
// is one billed function invocation, so the queue waits for a full batch or
// 20s, and always flushes when the page is hidden or unloaded.
const FIRST_PARTY_BATCH_SIZE = 20;
const FIRST_PARTY_FLUSH_DELAY_MS = 20_000;
let firstPartyQueue: FirstPartyFunnelPayload[] = [];
let firstPartyFlushTimer: ReturnType<typeof setTimeout> | null = null;
let firstPartyFlushInFlight = false;
let firstPartyLifecycleListenersInstalled = false;

function installFirstPartyLifecycleListeners(): void {
  if (firstPartyLifecycleListenersInstalled) return;
  firstPartyLifecycleListenersInstalled = true;
  window.addEventListener('pagehide', () => void flushFirstPartyFunnelEvents());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushFirstPartyFunnelEvents();
  });
}

function scheduleFirstPartyFlush(delay = FIRST_PARTY_FLUSH_DELAY_MS): void {
  if (firstPartyFlushTimer !== null) return;
  firstPartyFlushTimer = setTimeout(() => {
    firstPartyFlushTimer = null;
    void flushFirstPartyFunnelEvents();
  }, delay);
}

async function flushFirstPartyFunnelEvents(): Promise<void> {
  if (firstPartyFlushInFlight || firstPartyQueue.length === 0) return;
  firstPartyFlushInFlight = true;
  const events = firstPartyQueue.splice(0, FIRST_PARTY_BATCH_SIZE);

  try {
    const proof = await getRequestProof();
    await fetch('/api/funnel/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-TLS-Request-Proof': proof,
      },
      body: JSON.stringify({ events }),
      keepalive: true,
    });
  } catch {
    // Analytics failures never block the user. An unauthenticated beacon is
    // intentionally not attempted because the route would reject it.
  } finally {
    firstPartyFlushInFlight = false;
    if (firstPartyQueue.length > 0) scheduleFirstPartyFlush(0);
  }
}

export async function __flushFirstPartyFunnelEventsForTests(): Promise<void> {
  if (firstPartyFlushTimer !== null) {
    clearTimeout(firstPartyFlushTimer);
    firstPartyFlushTimer = null;
  }
  await flushFirstPartyFunnelEvents();
}

export function __resetFirstPartyFunnelQueueForTests(): void {
  if (firstPartyFlushTimer !== null) clearTimeout(firstPartyFlushTimer);
  firstPartyFlushTimer = null;
  firstPartyFlushInFlight = false;
  firstPartyQueue = [];
}

export function sendFirstPartyFunnelEvent(
  eventName: string,
  properties: Record<string, string | number | boolean>,
) {
  if (typeof window === 'undefined') return;
  if (!FIRST_PARTY_FUNNEL_EVENTS.has(eventName)) return;
  // Attach stable anonymous browser UUID so the nightly co-download matrix
  // can group events per-user instead of per-IP. See lib/tls-uid.ts.
  const tls_uid = getOrCreateTlsUid();
  const enrichedProps: Record<string, string | number | boolean> = tls_uid
    ? { ...properties, tls_uid }
    : properties;
  try {
    installFirstPartyLifecycleListeners();
    void getRequestProof().catch(() => {});
    firstPartyQueue.push({
      event: eventName,
      properties: enrichedProps,
      page: window.location.pathname,
      ts: Date.now(),
    });
    if (firstPartyQueue.length >= FIRST_PARTY_BATCH_SIZE) {
      void flushFirstPartyFunnelEvents();
    } else {
      scheduleFirstPartyFlush();
    }
  } catch {
    // Never block the user on analytics failures.
  }
}

// Safe GA4 event tracking (mirrors safeTrack in analytics.ts)
function safeGA4Event(
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined>
) {
  // Filter out undefined values once; we reuse the result for GA4 + first-party.
  const cleanProps = Object.fromEntries(
    Object.entries(properties || {}).filter(([, v]) => v !== undefined)
  ) as Record<string, string | number | boolean>;

  try {
    if (typeof window !== 'undefined' && window.gtag && isGA4TrackingEnabled()) {
      window.gtag('event', eventName, cleanProps);
    }
  } catch {
    // GA4 should never block core user flows
  }

  sendFirstPartyFunnelEvent(eventName, cleanProps);
}

// ============================================
// CONVERSION EVENTS (Primary KPI)
// ============================================

/**
 * Track download completion - PRIMARY KPI
 * Fires when user successfully downloads a sound file
 */
export function trackDownloadCompleted(props: {
  sound_id: string;
  sound_title: string;
  category?: string;
  source: DownloadSource;
  entry_source?: string;
  trim_used?: boolean;
}) {
  safeGA4Event('download_completed', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    category: props.category ?? '',
    source: props.source,
    entry_source: props.entry_source ?? '',
    trim_used: props.trim_used ?? false,
    event_category: 'conversion',
    event_label: props.sound_title,
  });
}

// ============================================
// FUNNEL EVENTS
// ============================================

/**
 * Track install guide view (intent signal)
 */
export function trackInstallGuideViewed(props: {
  source: string;
  entry_source?: string;
}) {
  safeGA4Event('install_guide_viewed', {
    source: props.source,
    entry_source: props.entry_source ?? '',
    event_category: 'intent',
  });
}

/**
 * Track that a user arrived at the install guide AFTER completing a download.
 * Fires once per session via a sessionStorage flag set by the download flow.
 */
export function trackGuideVisitedPostDownload(props: {
  sound_id?: string;
  sound_title?: string;
  download_source?: string;
  seconds_since_download?: number;
}) {
  safeGA4Event('guide_visited_post_download', {
    sound_id: props.sound_id ?? '',
    sound_title: props.sound_title ?? '',
    download_source: props.download_source ?? '',
    seconds_since_download: props.seconds_since_download ?? 0,
    event_category: 'funnel',
  });
}

/**
 * Track user self-reporting that their install succeeded or failed.
 * Lives at the bottom of /guide as a "Did the install work?" CTA.
 */
export function trackInstallSelfReportedComplete(props: {
  outcome: 'success' | 'failed' | 'needs_help';
  tesla_model?: string;
  came_from_download?: boolean;
  sound_id?: string;
  /** Which surface asked: 'guide' | 'receipt' | 'return_visit' | 'post_download_panel'. */
  source?: string;
}) {
  safeGA4Event('install_self_reported_complete', {
    outcome: props.outcome,
    tesla_model: props.tesla_model ?? '',
    came_from_download: props.came_from_download ?? false,
    ...(props.sound_id ? { sound_id: props.sound_id } : {}),
    ...(props.source ? { source: props.source } : {}),
    event_category: 'conversion',
  });
}

// ============================================
// TOP-OF-FUNNEL: LANDING VIEW (TES-1573)
// ============================================

const LANDING_VIEW_SESSION_KEY = 'tls.landing_view.v1';

// Two guards, because they cover different windows. The module-level flag
// covers a single page load (React StrictMode double-mounts effects in dev,
// and a remount of the tracker must not re-fire). sessionStorage covers the
// whole tab session, so a hard reload or a second entry page doesn't inflate
// the denominator we divide preview/download counts by.
let landingViewSentThisLoad = false;

/** Test-only: reset the per-load guard. */
export function __resetLandingViewGuardForTests(): void {
  landingViewSentThisLoad = false;
}

/**
 * Classify how the visitor arrived, reusing the referrer parsing that
 * lib/ga4-tracking.ts already owns so the two don't drift.
 * Returns one of: direct | organic | referral | internal.
 */
function getLandingEntrySource(): string {
  try {
    const referrer = getReferrerData();
    if (referrer.isDirect) return 'direct';
    if (referrer.referrer === 'internal') return 'internal';
    if (referrer.isOrganic) return 'organic';
    return 'referral';
  } catch {
    // Malformed document.referrer — still count the landing, just unlabelled.
    return 'unknown';
  }
}

/**
 * The referring hostname for this landing, or `direct` / `unknown` (TES-1858).
 *
 * Deliberately parsed from `document.referrer` here rather than routed through
 * getReferrerData(): that helper's contract is the four-way `entry_source`
 * classification, and widening it would put the session denominator at risk for
 * a key that only needs a hostname. See lib/referrer-host.ts for why we store
 * the full hostname instead of a registrable domain.
 */
function getLandingReferrerHost(): string {
  try {
    return referrerHostFromUrl(document.referrer, window.location.hostname);
  } catch {
    // Never let referrer parsing cost us the landing_view itself — that event
    // is the denominator every conversion rate in the funnel divides by.
    return REFERRER_HOST_UNKNOWN;
  }
}

/**
 * Fire the funnel's first step — once per browser session.
 *
 * Mounted globally (components/analytics/landing-view-tracker.tsx via the
 * layout shell) rather than per-page, so it counts sessions that land
 * anywhere on the site, not just on `/`. Client-side SPA navigations do not
 * re-fire it: the tracker lives in the root layout and mounts once per hard
 * load, and the sessionStorage guard catches reloads.
 *
 * The landing path itself is carried by the `page` field that
 * sendFirstPartyFunnelEvent attaches, so it isn't duplicated in properties.
 */
export function trackLandingView(): void {
  if (typeof window === 'undefined') return;
  if (landingViewSentThisLoad) return;

  try {
    if (sessionStorage.getItem(LANDING_VIEW_SESSION_KEY)) {
      landingViewSentThisLoad = true;
      return;
    }
    sessionStorage.setItem(LANDING_VIEW_SESSION_KEY, '1');
  } catch {
    // Private mode / storage disabled: fall back to the per-load guard alone.
    // Worst case a reload counts twice, which still beats counting zero.
  }

  landingViewSentThisLoad = true;

  safeGA4Event('landing_view', {
    entry_source: getLandingEntrySource(),
    referrer_host: getLandingReferrerHost(),
    event_category: 'engagement',
    non_interaction: true,
  });
}

/**
 * Track library page view
 */
export function trackLibraryPageViewed(props: {
  entry_source?: string;
  sort_by?: string;
  category?: string;
}) {
  safeGA4Event('library_page_viewed', {
    entry_source: props.entry_source ?? '',
    sort_by: props.sort_by ?? '',
    category: props.category ?? '',
    event_category: 'engagement',
  });
}

/**
 * Track sound detail click
 */
export function trackSoundDetailClick(props: {
  sound_id: string;
  sound_title: string;
  position?: number;
  source: string;
}) {
  safeGA4Event('sound_detail_click', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    position: props.position ?? 0,
    source: props.source,
    event_category: 'engagement',
  });
}

// ============================================
// ZERO RESULTS RECOVERY EVENTS
// ============================================

export function trackZeroResultsViewed(props: {
  query?: string;
  category: string;
  result_count: number;
}) {
  safeGA4Event('zero_results_viewed', {
    query: props.query ?? '',
    category: props.category,
    result_count: props.result_count,
    event_category: 'recovery',
  });
}

export function trackZeroResultsRecoveryClick(props: {
  action: 'request_sound' | 'vote_request' | 'browse_all' | 'clear_filters';
  destination: string;
}) {
  safeGA4Event('zero_results_recovery_click', {
    action: props.action,
    destination: props.destination,
    event_category: 'recovery',
  });
}

// ============================================
// TOOL ENTRY EVENTS
// ============================================

export function trackToolEntryCtaClick(props: {
  tool: 'converter' | 'voice_generator';
  source: string;
  destination: string;
}) {
  safeGA4Event('tool_entry_cta_click', {
    tool: props.tool,
    source: props.source,
    destination: props.destination,
    event_category: 'engagement',
  });
}

// ============================================
// DOWNLOAD BUTTON CLICK (Funnel Intent)
// ============================================

export function trackDownloadButtonClick(props: {
  sound_id: string;
  sound_title: string;
  category?: string;
  source: DownloadSource;
  entry_source?: string;
}) {
  safeGA4Event('download_button_click', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    category: props.category ?? '',
    source: props.source,
    entry_source: props.entry_source ?? '',
    event_category: 'funnel',
  });
}

// ============================================
// PREVIEW PLAY (Audio Engagement)
// ============================================

export function trackPreviewPlay(props: {
  sound_id: string;
  sound_title?: string;
  category?: string;
  source: 'sound_player' | 'sound_card' | 'mobile_player' | 'create_page' | 'voice_generator';
  autoplay?: boolean;
}) {
  safeGA4Event('preview_play', {
    sound_id: props.sound_id,
    sound_title: props.sound_title ?? '',
    category: props.category ?? '',
    source: props.source,
    autoplay: props.autoplay ?? false,
    event_category: 'engagement',
  });
}

/**
 * Fires when the user listened to ≥80% of a preview (signal: strong positive
 * interest). Mirrors trackPreviewPlay's shape so per-event funnel math stays
 * symmetric.
 */
export function trackPreviewCompleted(props: {
  sound_id: string;
  category?: string;
  source: 'sound_player' | 'sound_card' | 'mobile_player' | 'create_page' | 'voice_generator';
  played_pct: number;
}) {
  safeGA4Event('preview_completed', {
    sound_id: props.sound_id,
    category: props.category ?? '',
    source: props.source,
    played_pct: Math.round(props.played_pct * 100) / 100,
    event_category: 'engagement',
  });
}

/**
 * Fires when the user skipped a preview after <20% played (signal: negative
 * interest). Used as a downvote in lib/personalization.ts scoring.
 */
export function trackPreviewSkipped(props: {
  sound_id: string;
  category?: string;
  source: 'sound_player' | 'sound_card' | 'mobile_player' | 'create_page' | 'voice_generator';
  played_pct: number;
}) {
  safeGA4Event('preview_skipped', {
    sound_id: props.sound_id,
    category: props.category ?? '',
    source: props.source,
    played_pct: Math.round(props.played_pct * 100) / 100,
    event_category: 'engagement',
  });
}

// ============================================
// AUDIO PLAYER EVENTS
// ============================================

export function trackAudioPlay(props: {
  sound_id: string;
  source: 'sound_player' | 'sound_card' | 'mobile_player';
  autoplay?: boolean;
}) {
  safeGA4Event('audio_play', {
    sound_id: props.sound_id,
    source: props.source,
    autoplay: props.autoplay ?? false,
    event_category: 'engagement',
  });
}

// ============================================
// CONVERSION FUNNEL EVENTS
// ============================================

export function trackSoundPageViewed(props: {
  sound_id: string;
  sound_title: string;
  category: string | null;
}) {
  safeGA4Event('sound_page_viewed', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    category: props.category || 'uncategorized',
    event_category: 'engagement',
    non_interaction: true,
  });
}

export function trackDownloadInitiated(props: {
  sound_id: string;
  sound_title: string;
  category?: string | null;
  source: DownloadSource;
}) {
  safeGA4Event('download_initiated', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    category: props.category || 'uncategorized',
    source: props.source,
    event_category: 'conversion',
  });
}

export type { DownloadFailureReason };

/**
 * Buckets a download error into the coarse reasons the funnel reads. The
 * storage layer throws plain errors whose message carries the HTTP status text,
 * so classification is by message; anything unrecognised is a network failure.
 */
export function classifyDownloadFailure(error: unknown): DownloadFailureReason {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  const status = /\bhttp (\d{3})\b/.exec(message)?.[1];
  if (status) {
    if (status.startsWith('4')) return 'http_4xx';
    if (status.startsWith('5')) return 'http_5xx';
  }
  if (/private audio path|invalid audio file path|blocked|forbidden|not allowed/.test(message)) return 'blocked';
  if (/timeout|timed out|abort/.test(message)) return 'timeout';
  return 'network';
}

export function trackDownloadFailed(props: {
  sound_id?: string;
  source: string;
  error: unknown;
}) {
  safeGA4Event('download_failed', {
    sound_id: props.sound_id,
    source: props.source,
    reason: classifyDownloadFailure(props.error),
    event_category: 'conversion',
  });
}

/**
 * One settled library search (the caller debounces). Where consent is required
 * and analytics has not been granted, the search text is left out and only the
 * counts and filters are sent.
 */
export function trackLibrarySearch(props: {
  query: string;
  result_count: number;
  category?: string;
  model?: string;
  /** The list shown came from a typo correction or a loosened query. */
  rescued?: boolean;
  source: string;
}) {
  safeGA4Event('library_search', {
    ...(isAnalyticsAllowedNow() ? { query: normalizeSearchQuery(props.query) } : {}),
    result_count: props.result_count,
    category: props.category ?? '',
    model: props.model ?? '',
    ...(props.rescued ? { rescued: true } : {}),
    source: props.source,
    event_category: 'engagement',
  });
}

export function trackCreateStep(props: {
  tool: (typeof CREATE_TOOLS)[number];
  step: (typeof CREATE_STEPS)[number];
  reason?: string;
}) {
  safeGA4Event('create_step', {
    tool: props.tool,
    step: props.step,
    reason: props.reason,
    event_category: 'engagement',
  });
}

/** A guide section scrolled into view and held there. Fires once per section per page view. */
export function trackGuideSectionView(section: GuideSection) {
  safeGA4Event('guide_section_view', { section, event_category: 'engagement', non_interaction: true });
}

/**
 * A troubleshooting entry brought into view and held there. The entries are
 * always expanded, so "open" means the visitor reached it. Fires once per
 * entry per page view.
 */
export function trackGuideTroubleshootOpen(problem: GuideProblem) {
  safeGA4Event('guide_troubleshoot_open', { problem, event_category: 'engagement', non_interaction: true });
}

/** A "Check my car" answer. Model and verdict are closed sets; the year is a bounded number. */
export function trackGuideCarCheck(props: { model: string; model_year: number; verdict: CarStatus }) {
  safeGA4Event('guide_car_check', {
    model: props.model,
    model_year: props.model_year,
    verdict: props.verdict,
    event_category: 'engagement',
  });
}

/**
 * How one ad slot ended up, once per slot per page view. Read it against
 * downloads per visitor on the same page to see whether fill hurts conversion.
 * The page is the event's own `page`; `ad_slot` tells two slots on one page apart.
 */
export function trackAdSlotState(props: { state: AdSlotState; ad_slot: string }) {
  safeGA4Event('ad_slot_state', {
    state: props.state,
    ad_slot: props.ad_slot,
    event_category: 'ads',
    non_interaction: true,
  });
}

export function trackFaqPageViewed(entrySource: string) {
  safeGA4Event('faq_page_viewed', {
    event_category: 'engagement',
    event_label: entrySource,
  });
}

// ============================================
// EXPERIMENT EVENTS
// ============================================

// No call site yet: wire this where an A/B variant is first rendered, or no
// experiment can be analysed.

export function trackExperimentExposure(props: {
  experiment_id: string;
  variant: string;
}) {
  safeGA4Event('experiment_exposure', {
    experiment_id: props.experiment_id,
    variant: props.variant,
    event_category: 'experiment',
    non_interaction: true,
  });
}

/**
 * Track "Email me this sound" request (post-download panel, sound page,
 * or post-download receipt — distinguished by email_surface).
 */
export function trackEmailFileRequested(props: {
  sound_id: string;
  join_newsletter: boolean;
  /** UI surface the request came from: 'sound_page' | 'post_download_receipt' | 'post_download_panel'. */
  email_surface?: string;
}) {
  safeGA4Event('email_file_requested', {
    sound_id: props.sound_id,
    join_newsletter: props.join_newsletter,
    ...(props.email_surface ? { email_surface: props.email_surface } : {}),
    event_category: 'conversion',
  });
}

/**
 * The "email me this sound" form was opened by the visitor (not mounted open).
 * Denominator for email_file_requested, so open-but-never-sent is visible.
 */
export function trackEmailFormOpened(props: { email_surface: string }) {
  safeGA4Event('email_form_opened', {
    email_surface: props.email_surface,
    event_category: 'engagement',
  });
}

/** The newsletter field was shown after the visitor said the install worked. */
export function trackNewsletterOfferShown(props: { surface: OfferSurface }) {
  safeGA4Event('newsletter_offer_shown', {
    offer_surface: props.surface,
    event_category: 'engagement',
    non_interaction: true,
  });
}

/** A newsletter signup the visitor completed from an in-flow offer. */
export function trackNewsletterSignup(props: { surface: OfferSurface }) {
  safeGA4Event('newsletter_signup', {
    offer_surface: props.surface,
    event_category: 'conversion',
  });
}

/** "Email me this list" on /sounds/saved; `result_count` is how many sounds were in it. */
export function trackSavedListEmailed(props: { result_count: number }) {
  safeGA4Event('saved_list_emailed', {
    result_count: props.result_count,
    event_category: 'conversion',
  });
}

/** Click on the returning-visitor "N new since" line; `result_count` is the number shown. */
export function trackNewSinceLineClick(props: { result_count: number }) {
  safeGA4Event('new_since_line_click', {
    result_count: props.result_count,
    event_category: 'engagement',
  });
}

/**
 * Fires when a shareable referral URL is rendered for the user
 * (share-card opens or header share button is hit). Pairs with
 * referral_link_copied to give a "share funnel" read.
 *
 * The `*_toast` values outlived their toasts — they are retained verbatim so
 * historical dashboards keep their meaning (see lib/post-download-confirmation.ts).
 * `mobile_tray_inline` is the full-screen "Now Playing" tray (TES-2248); it is
 * deliberately NOT folded into `mobile_player_toast`, which the docked mini
 * strip still reports, because the two differ in how much the visitor has
 * already committed by the time they see the share row.
 */
export function trackReferralLinkGenerated(props: {
  sound_id: string;
  share_surface: 'share_setup_card' | 'header_button' | 'post_download_receipt' | 'sound_card_toast' | 'mobile_player_toast' | 'mobile_tray_inline';
}) {
  safeGA4Event('referral_link_generated', {
    sound_id: props.sound_id,
    share_surface: props.share_surface,
    event_category: 'share',
  });
}

/**
 * Fires when the user successfully copies the share link OR completes
 * a native navigator.share() flow. `share_method` distinguishes the
 * two paths.
 */
export function trackReferralLinkCopied(props: {
  sound_id: string;
  share_surface: 'share_setup_card' | 'header_button' | 'post_download_receipt' | 'sound_card_toast' | 'mobile_player_toast' | 'mobile_tray_inline';
  share_method: 'clipboard' | 'native_share';
}) {
  safeGA4Event('referral_link_copied', {
    sound_id: props.sound_id,
    share_surface: props.share_surface,
    share_method: props.share_method,
    event_category: 'share',
  });
}

/**
 * Fires once when the post-download confirmation moment is shown to the user
 * (the "your sound is downloaded" surface). Lets us measure how many downloads
 * actually surface the install nudge + share-back loop.
 *
 * `surface` distinguishes the four placements, which differ enormously in how
 * much install guidance they carry — an impression on one is not worth the same
 * as an impression on another, so never read the event un-split:
 *   - 'sound_detail'       — <PostDownloadReceipt/> on /sounds/[id]; inlines all
 *                            3 install steps (richest surface)
 *   - 'chime_studio'       — <PostDownloadPanel/> in Create / Chime Studio
 *   - 'sound_card_toast'   — the transient Sonner toast fired by the library-grid
 *                            card download (TES-1577); one line + a /guide link
 *   - 'mobile_player_toast'— the same toast from the mobile mini-player
 * The two toast surfaces carry the majority of site-wide download volume, so
 * before TES-1577 this event covered ~11% of downloads and G3 (install
 * confidence) had no signal for the rest.
 */
export function trackDownloadConfirmShown(props: {
  sound_id: string;
  sound_title: string;
  surface: 'sound_detail' | 'chime_studio' | 'sound_card_toast' | 'sound_card_inline' | 'mobile_player_toast' | 'mobile_player_inline';
}) {
  safeGA4Event('download_confirm_shown', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    surface: props.surface,
    event_category: 'funnel',
  });
}

/**
 * Fires when the user clicks a share-back CTA in the post-download panel —
 * a pre-filled X / Reddit post telling other Tesla owners which sound they
 * picked. Feeds the viral/referral loop (G1.4).
 */
export function trackShareBackClick(props: {
  sound_id: string;
  sound_title: string;
  channel: 'x' | 'reddit';
}) {
  safeGA4Event('share_back_click', {
    sound_id: props.sound_id,
    sound_title: props.sound_title,
    channel: props.channel,
    event_category: 'share',
  });
}
